import React, { useState } from 'react';
import { AIStudyPlan as AIStudyPlanType, AIStudySession } from '../types';
import { 
  CalendarIcon, 
  ClockIcon, 
  BookOpenIcon, 
  PencilIcon,
  TrashIcon,
  PlusIcon
} from '@heroicons/react/24/outline';

interface AIStudyPlanProps {
  studyPlan: AIStudyPlanType;
  onUpdatePlan: (planId: string, updates: Partial<AIStudyPlanType>) => void;
  onDeletePlan?: (planId: string) => void;
}

const AIStudyPlan: React.FC<AIStudyPlanProps> = ({
  studyPlan,
  onUpdatePlan,
  onDeletePlan
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editingSession, setEditingSession] = useState<AIStudySession | null>(null);
  const [showAddSession, setShowAddSession] = useState(false);

  const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  const handleToggleActive = () => {
    onUpdatePlan(studyPlan.id, { isActive: !studyPlan.isActive });
  };



  const handleUpdateSession = (sessionId: string, updates: Partial<AIStudySession>) => {
    const updatedSessions = studyPlan.dailySchedule.map((session: AIStudySession) =>
      session.id === sessionId ? { ...session, ...updates } : session
    );
    onUpdatePlan(studyPlan.id, { dailySchedule: updatedSessions });
    setEditingSession(null);
  };

  const handleAddSession = (newSession: Omit<AIStudySession, 'id'>) => {
    const session: AIStudySession = {
      ...newSession,
      id: `session_${Date.now()}`
    };
    const updatedSessions = [...studyPlan.dailySchedule, session];
    onUpdatePlan(studyPlan.id, { dailySchedule: updatedSessions });
    setShowAddSession(false);
  };

  const handleDeleteSession = (sessionId: string) => {
    const updatedSessions = studyPlan.dailySchedule.filter((session: AIStudySession) => session.id !== sessionId);
    onUpdatePlan(studyPlan.id, { dailySchedule: updatedSessions });
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner':
        return 'bg-green-100 text-green-800';
      case 'intermediate':
        return 'bg-yellow-100 text-yellow-800';
      case 'advanced':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatTime = (time: string) => {
    return time; // Assuming time is already in HH:MM format
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins > 0 ? `${mins}m` : ''}`;
    }
    return `${mins}m`;
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-2">
            <h3 className="text-xl font-semibold text-gray-900">{studyPlan.title}</h3>
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getDifficultyColor(studyPlan.difficulty)}`}>
              {studyPlan.difficulty.charAt(0).toUpperCase() + studyPlan.difficulty.slice(1)}
            </span>
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
              studyPlan.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
            }`}>
              {studyPlan.isActive ? 'Active' : 'Inactive'}
            </span>
          </div>
          <p className="text-gray-600 mb-2">{studyPlan.description}</p>
          <div className="flex items-center space-x-4 text-sm text-gray-500">
            <span className="flex items-center">
              <CalendarIcon className="w-4 h-4 mr-1" />
              {studyPlan.estimatedDuration} hours estimated
            </span>
            <span className="flex items-center">
              <ClockIcon className="w-4 h-4 mr-1" />
              Updated {new Date(studyPlan.updatedAt).toLocaleDateString()}
            </span>
          </div>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={handleToggleActive}
            className={`px-3 py-1 text-sm rounded-md transition-colors ${
              studyPlan.isActive
                ? 'bg-red-100 text-red-700 hover:bg-red-200'
                : 'bg-green-100 text-green-700 hover:bg-green-200'
            }`}
          >
            {studyPlan.isActive ? 'Deactivate' : 'Activate'}
          </button>
          <button
            onClick={() => setIsEditing(!isEditing)}
            className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors"
          >
            <PencilIcon className="w-4 h-4" />
          </button>
          {onDeletePlan && (
            <button
              onClick={() => onDeletePlan(studyPlan.id)}
              className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors"
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Weekly Goals */}
      <div className="mb-6">
        <h4 className="text-lg font-medium text-gray-900 mb-3">Weekly Goals</h4>
        <div className="grid gap-2 md:grid-cols-2">
          {studyPlan.weeklyGoals.map((goal: string, index: number) => (
            <div key={index} className="flex items-start">
              <span className="inline-flex items-center justify-center w-6 h-6 bg-blue-200 text-blue-800 text-xs font-medium rounded-full mr-3">
                {index + 1}
              </span>
              <span className="text-sm text-gray-700">{goal}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Daily Schedule */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-lg font-medium text-gray-900">Daily Schedule</h4>
          <button
            onClick={() => setShowAddSession(true)}
            className="px-3 py-1 text-sm bg-green-100 text-green-700 rounded-md hover:bg-green-200 transition-colors flex items-center"
          >
            <PlusIcon className="w-4 h-4 mr-1" />
            Add Session
          </button>
        </div>
        
        <div className="space-y-3">
          {studyPlan.dailySchedule.map((session: AIStudySession) => (
            <div key={session.id} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <span className="font-medium text-gray-900">
                    {daysOfWeek[session.dayOfWeek]}
                  </span>
                  <span className="text-sm text-gray-500">
                    {formatTime(session.startTime)} • {formatDuration(session.duration)}
                  </span>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setEditingSession(session)}
                    className="text-blue-600 hover:text-blue-800 text-sm"
                  >
                    <PencilIcon className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteSession(session.id)}
                    className="text-red-600 hover:text-red-800 text-sm"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              <div className="grid gap-3 md:grid-cols-2">
                <div>
                  <h6 className="text-sm font-medium text-gray-700 mb-2">Activities</h6>
                  <ul className="space-y-1">
                    {session.activities.map((activity: string, index: number) => (
                      <li key={index} className="flex items-start text-sm text-gray-600">
                        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full mr-2 mt-2 flex-shrink-0"></div>
                        {activity}
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div>
                  <h6 className="text-sm font-medium text-gray-700 mb-2">Materials</h6>
                  <ul className="space-y-1">
                    {session.materials.map((material: string, index: number) => (
                      <li key={index} className="flex items-start text-sm text-gray-600">
                        <BookOpenIcon className="w-4 h-4 text-gray-400 mr-2 mt-0.5" />
                        {material}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Edit Session Modal */}
      {editingSession && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 max-w-md">
            <h4 className="text-lg font-medium text-gray-900 mb-4">Edit Study Session</h4>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Day of Week</label>
                <select
                  value={editingSession.dayOfWeek}
                  onChange={(e) => setEditingSession({
                    ...editingSession,
                    dayOfWeek: parseInt(e.target.value)
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {daysOfWeek.map((day, index) => (
                    <option key={index} value={index}>{day}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
                <input
                  type="time"
                  value={editingSession.startTime}
                  onChange={(e) => setEditingSession({
                    ...editingSession,
                    startTime: e.target.value
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Duration (minutes)</label>
                <input
                  type="number"
                  value={editingSession.duration}
                  onChange={(e) => setEditingSession({
                    ...editingSession,
                    duration: parseInt(e.target.value)
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            
            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => handleUpdateSession(editingSession.id, editingSession)}
                className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors"
              >
                Save Changes
              </button>
              <button
                onClick={() => setEditingSession(null)}
                className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Session Modal */}
      {showAddSession && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 max-w-md">
            <h4 className="text-lg font-medium text-gray-900 mb-4">Add Study Session</h4>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Day of Week</label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  id="new-session-day"
                >
                  {daysOfWeek.map((day, index) => (
                    <option key={index} value={index}>{day}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
                <input
                  type="time"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  id="new-session-time"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Duration (minutes)</label>
                <input
                  type="number"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  id="new-session-duration"
                  defaultValue={60}
                />
              </div>
            </div>
            
            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => {
                  const daySelect = document.getElementById('new-session-day') as HTMLSelectElement;
                  const timeInput = document.getElementById('new-session-time') as HTMLInputElement;
                  const durationInput = document.getElementById('new-session-duration') as HTMLInputElement;
                  
                  if (daySelect && timeInput && durationInput) {
                    handleAddSession({
                      dayOfWeek: parseInt(daySelect.value),
                      startTime: timeInput.value,
                      duration: parseInt(durationInput.value),
                      activities: ['Review previous material', 'Read new content', 'Practice exercises'],
                      materials: ['Course textbook', 'Practice workbook', 'Online resources']
                    });
                  }
                }}
                className="flex-1 bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 transition-colors"
              >
                Add Session
              </button>
              <button
                onClick={() => setShowAddSession(false)}
                className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIStudyPlan;
